import { formatToTimeZone } from '@zenchef/date-fns-timezone'
import { Icon, Text } from '@zenchef/ds-react'
import { HStack, Stack } from '@zenchef/styled-system/jsx'
import { addMonths } from 'date-fns'
import addDays from 'date-fns/add_days'
import { observer } from 'mobx-react-lite'
import { useCallback, useContext, useEffect, useState } from 'react'

import { CalendarPicker } from '@/components/DatePicker'
import Collapsible from '@/components/redesign/common/Collapsible/Collapsible'
import { selectableItemRecipe } from '@/components/redesign/common/SelectableItem'
import DayButton from '@/components/redesign/filters/DayInput/DayButton'
import MoreInfoAboutDate from '@/components/redesign/filters/DayInput/MoreInfoAboutDate'
import HighlightedExperienceBadge from '@/components/redesign/offers/HighlightedExperiences/HighlightedExperienceBadge'
import { ANALYTICS_EVENTS_NAMES, fireAnalyticEvent } from '@/lib/GoogleAnalytics'
import { useTranslation } from '@/utils/hooks'
import StoresContext from '@/utils/StoresContext'

interface DayInputProps {
  handleDaySelected: (day: string) => void
  handleClose: () => void
  isLoading?: boolean
}

const NUMBER_OF_NEXT_AVAILABLE_DATES_TO_SHOW = 2

const DayInput = observer(({ handleDaySelected, handleClose, isLoading }: DayInputProps) => {
  const { appStore } = useContext(StoresContext)
  const { t } = useTranslation()
  const [isCalendarVisible, setIsCalendarVisible] = useState(false)

  const { selectedHighlightedOffer } = appStore.state

  const today = formatToTimeZone(new Date(), 'YYYY-MM-DD', { timeZone: appStore.state.restaurantTimezone ?? 'UTC' })

  const nextAvailableDates = Array.from({ length: NUMBER_OF_NEXT_AVAILABLE_DATES_TO_SHOW }, (_, i) =>
    appStore.state.nextAvailableDateAtIndex(i)
  )

  const someMonthIsLoaded = appStore.state.months.length > 0
  const wishDayInNextAvailabilities = nextAvailableDates.includes(appStore.state.wish.day)
  const noAvailabilityFound = appStore.state.daysAvailableForCurrentPax.length === 0

  useEffect(() => {
    if (
      !isLoading &&
      someMonthIsLoaded &&
      ((!wishDayInNextAvailabilities && !isCalendarVisible) || noAvailabilityFound)
    ) {
      setIsCalendarVisible(true)
    }
  }, [wishDayInNextAvailabilities, isCalendarVisible, isLoading, noAvailabilityFound, someMonthIsLoaded])

  const handleMonthChange = (date) => {
    const d = new window.Date(date)
    const m = d.getMonth() + 1
    const y = d.getFullYear()
    appStore.getAvailabilitiesSummary(m, y)
    const followingMonth = addMonths(d, 1)
    const m2 = followingMonth.getMonth() + 1
    const y2 = followingMonth.getFullYear()
    appStore.getAvailabilitiesSummary(m2, y2)
  }

  const handleCloseAndHideCalendar = () => {
    handleClose()
    setIsCalendarVisible(false)
  }

  const handleOpenCalendar = useCallback(() => {
    const [year, month] = appStore.state.currentMonthKey.split('-')
    if (appStore.state.currentMonth?.partial) {
      appStore.getAvailabilitiesSummary(month, year)
    }

    fireAnalyticEvent(ANALYTICS_EVENTS_NAMES.OPEN_CALENDAR)
  }, [appStore])

  return (
    <Stack width='100%' height='100%' justify='center' gap='gap.4'>
      <Collapsible
        isCollapsed={!isCalendarVisible}
        toggle={() => {
          if (!isCalendarVisible) {
            handleOpenCalendar()
          }
          setIsCalendarVisible(!isCalendarVisible)
        }}>
        <HStack gap='gap.1' width='100%' alignItems='stretch'>
          {nextAvailableDates.map((date, index) => (
            <DayButton
              day={
                date ??
                formatToTimeZone(addDays(today, index), 'YYYY-MM-DD', {
                  timeZone: appStore.state.restaurantTimezone ?? 'UTC'
                })
              }
              key={date ?? `default-date-${index}`}
              isLoading={isLoading}
              handleDaySelected={handleDaySelected}
              handleClose={handleCloseAndHideCalendar}
            />
          ))}
          <Collapsible.Header
            className={selectableItemRecipe({ horizontal: true })}
            data-active={isCalendarVisible || undefined}>
            <Icon name='calendar' />
            <Text textStyle='paragraph.m.regular'>{t('next_available_date.other')}</Text>
          </Collapsible.Header>
        </HStack>
        <Collapsible.Content maxHeight='600px' marginTop={isCalendarVisible ? undefined : '-gap.4'}>
          {selectedHighlightedOffer ? (
            <HighlightedExperienceBadge mb='gap.4'>{t('experience_available')}</HighlightedExperienceBadge>
          ) : null}
          <CalendarPicker
            privatisation={false}
            handleMonthChange={handleMonthChange}
            handleDaySelected={handleDaySelected}
            close={handleClose}
          />
        </Collapsible.Content>
      </Collapsible>
      <MoreInfoAboutDate handleDaySelected={handleDaySelected} isLoadingNextAvailableDate={isLoading ?? false} />
    </Stack>
  )
})

export default DayInput
